import { Component, input, output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  ButtonComponent,
  CheckboxComponent,
  IconComponent,
  InputComponent,
  TooltipComponent,
  TypographyComponent,
} from '@purolator/ui-kit-angular';
import { TranslatePipe } from '../../../../../core/i18n';
import type { PackagePieceRowGroup } from '../../../forms/package-selection.form';
import {
  blurCustomerPieceField,
  customerDimensionUnitLabelKey,
  customerPieceFieldErrorKey,
  customerPieceFieldHasError,
  customerWeightUnitLabelKey,
} from '../../../customer-piece-field';
import { PackageMeasureInputDirective } from '../../../package-measure-input.directive';
import type { PackageUnitSystem } from '../../../package-types.config';

/**
 * Single Customer Packaging tablet card (Figma 17480:39157) for one package row.
 */
@Component({
  selector: 'app-customer-packaging-card',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    CheckboxComponent,
    IconComponent,
    InputComponent,
    PackageMeasureInputDirective,
    TooltipComponent,
    TypographyComponent,
    TranslatePipe,
  ],
  templateUrl: './customer-packaging-card.html',
  styleUrl: './customer-packaging-card.scss',
})
export class CustomerPackagingCard {
  readonly row = input.required<PackagePieceRowGroup>();
  readonly index = input.required<number>();
  readonly pieceCount = input.required<number>();
  /** Remount key so Clear recreates puro-input hosts after reset. */
  readonly syncKey = input.required<string>();
  readonly unitSystem = input.required<PackageUnitSystem>();
  readonly showValidationErrors = input(false);

  readonly clearRow = output<number>();
  readonly removeRow = output<number>();

  /**
   * Weight unit label for the per-piece weight adornment.
   */
  protected weightUnitLabelKey(): string {
    return customerWeightUnitLabelKey(this.unitSystem());
  }

  /**
   * Dimension unit label for the dimension heading chip.
   */
  protected dimensionUnitLabelKey(): string {
    return customerDimensionUnitLabelKey(this.unitSystem());
  }

  /**
   * Returns whether a control should show an error state (touched or after Add/Save).
   */
  protected hasError(controlName: keyof PackagePieceRowGroup['controls']): boolean {
    return customerPieceFieldHasError(
      this.row().controls[controlName],
      this.showValidationErrors(),
    );
  }

  /**
   * Maps field validation errors to the inline message key.
   */
  protected fieldErrorKey(controlName: keyof PackagePieceRowGroup['controls']): string {
    return customerPieceFieldErrorKey(this.row().controls[controlName]);
  }

  /**
   * Marks a control touched on blur so Required / Exceeds limit appear.
   */
  protected onFieldBlur(controlName: keyof PackagePieceRowGroup['controls']): void {
    blurCustomerPieceField(this.row().controls[controlName], controlName);
  }
}
